/** @file ikFromTo: the minimal rotation between two unit vectors (private stand-in for math M1 quatFromTo). */
import type { Quat } from '@zilo/pose3d-math/quat/quat';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { ikCross } from '@zilo/pose3d-pose/_internal/ikCross';
import { ikDot } from '@zilo/pose3d-pose/_internal/ikDot';
import { ikNormalize } from '@zilo/pose3d-pose/_internal/ikNormalize';

/** Below this `1 + a.b` the vectors count as antiparallel. */
const ANTI = 1e-12;

/**
 * The shortest rotation taking unit `a` to unit `b`: `normalize([a x b, 1 + a.b])`; antiparallel -> 180 degrees about
 * an axis perpendicular to `a` (x, else y).
 */
export function ikFromTo(a: Vec3, b: Vec3): Quat {
  const w = 1 + ikDot(a, b);
  if (w < ANTI) {
    const axis = ikNormalize(Math.abs(a[0]) < 0.9 ? ikCross(a, [1, 0, 0]) : ikCross(a, [0, 1, 0]));
    return [axis[0], axis[1], axis[2], 0];
  }
  const [x, y, z] = ikCross(a, b);
  const n = Math.hypot(x, y, z, w);
  return [x / n, y / n, z / n, w / n];
}
